import React, { useState, useEffect } from 'react';
import { Calendar, Clock, BookOpen, User, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { formatTime12Hour, formatDate, formatDateTime12Hour } from '../utils/timeFormatting';

interface TimeSlot {
  id: string;
  date: string;
  startTime: string;
  endTime: string | null; // null for variable duration slots
  available: boolean;
  canBook30Min?: boolean;
  canBook1Hour?: boolean;
  canBook90Min?: boolean;
  canBook2Hours?: boolean;
  tutor: {
    id: string;
    name: string;
    email: string;
  };
  subject?: {
    id: string;
    name: string;
    onlineRate: number;
    inPersonRate: number;
  };
  availabilityId: string;
}

interface DayGrid {
  date: string;
  dayOfWeek: number;
  slots: TimeSlot[];
}

interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  onlineRate: number;
  inPersonRate: number;
}

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

const SchedulingGrid: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [weeklyGrid, setWeeklyGrid] = useState<DayGrid[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<0.5 | 1 | 1.5 | 2 | null>(null);
  const [selectedSubject1, setSelectedSubject1] = useState<string>('');
  const [selectedSubject2, setSelectedSubject2] = useState<string>('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [isRepeatLesson, setIsRepeatLesson] = useState(false);
  const [repeatWeeks, setRepeatWeeks] = useState(4);
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [currentMobileDay, setCurrentMobileDay] = useState(new Date());
  
  // Touch/Swipe handling state
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    // Adjust for Monday as first day (0 = Sunday, so we need to shift)
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const diff = d.getDate() + mondayOffset;
    return new Date(d.setDate(diff));
  }

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    fetchWeeklyGrid();
  }, [currentWeekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSubjects = async () => {
    try {
      // Students should only see their selected subjects with pricing info
      if (user?.role === 'STUDENT') {
        const response = await api.get('/subjects/student/my-subjects');
        setSubjects(response.data);
      } else {
        // Tutors/admins can see all subjects
        const response = await api.get('/subjects');
        setSubjects(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setError('Failed to load subjects');
    }
  };

  const fetchWeeklyGrid = async () => {
    try {
      setGridLoading(true);
      const response = await api.get('/availability/grid', {
        params: {
          startDate: currentWeekStart.toISOString().split('T')[0]
        }
      });
      // Process the response to ensure all slots have availability properties
      const processedGrid = response.data.map((day: DayGrid) => ({
        ...day,
        slots: day.slots.map((slot: TimeSlot) => ({
          ...slot,
          // Default all slots to support new durations until backend is updated
          canBook30Min: slot.canBook30Min ?? true,
          canBook90Min: slot.canBook90Min ?? true,
        }))
      }));
      setWeeklyGrid(processedGrid);
    } catch (error) {
      console.error('Failed to fetch weekly grid:', error);
      setError('Failed to load schedule. Please try again later.');
    } finally {
      setGridLoading(false);
    }
  };

  const handleSlotClick = (slot: TimeSlot) => {
    if (!slot.available) return;
    setSelectedSlot(slot);
    setSelectedDuration(null);
    setSelectedSubject1('');
    setSelectedSubject2('');
    setError('');
    setSuccess('');
  };

  const handleBookingRequest = async () => {
    if (!selectedSlot || !selectedDuration || !user) return;
    
    // Validate subject selection based on duration
    if ((selectedDuration === 0.5 || selectedDuration === 1 || selectedDuration === 1.5) && !selectedSubject1) {
      const durationText = selectedDuration === 0.5 ? '30-minute' : selectedDuration === 1 ? '1-hour' : '90-minute';
      setError(`Please select a subject for your ${durationText} lesson.`);
      return;
    }
    
    if (selectedDuration === 2 && (!selectedSubject1 || !selectedSubject2)) {
      setError('Please select two subjects for your 2-hour lesson.');
      return;
    }
    
    // Check if selected duration is available
    if (selectedDuration === 0.5 && !selectedSlot.canBook30Min) {
      setError('30-minute lessons are not available at this time slot.');
      return;
    }
    
    if (selectedDuration === 1.5 && !selectedSlot.canBook90Min) {
      setError('90-minute lessons are not available at this time slot.');
      return;
    }
    
    if (selectedDuration === 2 && !selectedSlot.canBook2Hours) {
      setError('2-hour lessons are not available at this time slot.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const bookingPromises = [];
      
      // Handle recurring lessons
      const numberOfBookings = isRepeatLesson ? repeatWeeks : 1;
      
      for (let week = 0; week < numberOfBookings; week++) {
        // Calculate dates for this week
        const baseStartTime = new Date(selectedSlot.startTime);
        const weekStartTime = new Date(baseStartTime);
        weekStartTime.setDate(baseStartTime.getDate() + (week * 7));
        
        const weekEndTime = new Date(weekStartTime);
        weekEndTime.setHours(weekEndTime.getHours() + selectedDuration);

        const weekNotes = isRepeatLesson && numberOfBookings > 1 
          ? `${bookingNotes} (Week ${week + 1} of ${numberOfBookings})`
          : bookingNotes;

        if (selectedDuration === 0.5 || selectedDuration === 1 || selectedDuration === 1.5) {
          // Single subject booking
          bookingPromises.push(
            api.post('/bookings/request', {
              tutorId: selectedSlot.tutor.id,
              subjectId: selectedSubject1,
              startTime: weekStartTime.toISOString(),
              endTime: weekEndTime.toISOString(),
              notes: weekNotes,
              isOnline: isOnline
            })
          );
        } else {
          // Two-hour booking with two subjects - create two back-to-back bookings
          const midTime = new Date(weekStartTime);
          midTime.setHours(midTime.getHours() + 1);

          // First hour
          bookingPromises.push(
            api.post('/bookings/request', {
              tutorId: selectedSlot.tutor.id,
              subjectId: selectedSubject1,
              startTime: weekStartTime.toISOString(),
              endTime: midTime.toISOString(),
              notes: `${weekNotes} (Part 1 of 2-hour lesson)`,
              isOnline: isOnline
            })
          );

          // Second hour
          bookingPromises.push(
            api.post('/bookings/request', {
              tutorId: selectedSlot.tutor.id,
              subjectId: selectedSubject2,
              startTime: midTime.toISOString(),
              endTime: weekEndTime.toISOString(),
              notes: `${weekNotes} (Part 2 of 2-hour lesson)`,
              isOnline: isOnline
            })
          );
        }
      }

      await Promise.all(bookingPromises);
      
      const successMessage = isRepeatLesson 
        ? `${numberOfBookings} recurring lesson requests sent successfully! The tutor will review and respond to each request.`
        : 'Booking request sent successfully! The tutor will review and respond to your request.';
      
      setSuccess(successMessage);
      setSelectedSlot(null);
      setSelectedDuration(null);
      setSelectedSubject1('');
      setSelectedSubject2('');
      setBookingNotes('');
      setIsRepeatLesson(false);
      setRepeatWeeks(4);
      setIsOnline(false);
      
      // Refresh the grid to show updated availability
      fetchWeeklyGrid();
      
      // Hide success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send booking request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newDate);
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentMobileDay);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    setCurrentMobileDay(newDate);
    
    // Update the weekly grid if we've navigated to a different week
    const newWeekStart = getWeekStart(newDate);
    if (newWeekStart.getTime() !== currentWeekStart.getTime()) {
      setCurrentWeekStart(newWeekStart);
    }
  };

  // Swipe gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const touch = e.targetTouches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchEnd(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const touch = e.targetTouches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = () => {
    if (!isMobile || !touchStart || !touchEnd) return;

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Minimum swipe distance (in pixels)
    const minSwipeDistance = 50;
    
    // Only trigger if horizontal swipe is dominant and meets minimum distance
    if (absDeltaX > absDeltaY && absDeltaX > minSwipeDistance) {
      if (deltaX > 0) {
        // Swipe left - go to next day
        navigateDay('next');
      } else {
        // Swipe right - go to previous day
        navigateDay('prev');
      }
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  const getCurrentDayData = () => {
    if (!isMobile) return null;
    
    const currentDayString = currentMobileDay.toISOString().split('T')[0];
    return weeklyGrid.find(day => day.date.split('T')[0] === currentDayString);
  };

  const formatTime = (dateString: string) => {
    return formatTime12Hour(dateString);
  };

  const formatSubjectCategory = (category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY') => {
    switch (category) {
      case 'ELEVEN_PLUS':
        return '11+';
      case 'GCSE':
        return 'GCSE';
      case 'A_LEVEL':
        return 'A-Level';
      case 'UNIVERSITY':
        return 'University';
      default:
        return category;
    }
  };

  const formatSubjectWithLevel = (subject: Subject) => {
    const baseFormat = `${subject.name} - ${formatSubjectCategory(subject.category)}`;
    
    // For students, show pricing information for both online and in-person
    if (user?.role === 'STUDENT' && subject.onlineRate && subject.inPersonRate) {
      return `${baseFormat} (Online: £${subject.onlineRate}/hr, In-person: £${subject.inPersonRate}/hr)`;
    }
    
    return baseFormat;
  };

  if (!user || user.role !== 'STUDENT') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-cream border border-rule rounded-card p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-ochre mr-3" />
          <p className="text-ink">Please log in as a student to view the scheduling grid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-ink mb-4">Book a Lesson</h1>
        <p className="text-muted">
          Select an available time slot below to request a lesson with Ethan.
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-card p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
          <p className="text-emerald-800">{success}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-card p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Navigation */}
      {isMobile ? (
        /* Mobile Day Navigation */
        <div className="mb-6 bg-paper border border-rule rounded-card p-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigateDay('prev')}
              className="p-2 rounded-card bg-cream hover:bg-cream/70 transition-colors"
              aria-label="Previous day"
            >
              <ChevronLeft className="w-5 h-5 text-ink" />
            </button>

            <div className="text-center">
              <h2 className="text-lg font-semibold text-ink">
                {currentMobileDay.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </h2>
              <p className="text-sm text-muted">
                {currentMobileDay.toLocaleDateString()}
              </p>
              <p className="text-xs text-muted mt-1">
                Swipe left/right to navigate days
              </p>
            </div>

            <button
              onClick={() => navigateDay('next')}
              className="p-2 rounded-card bg-cream hover:bg-cream/70 transition-colors"
              aria-label="Next day"
            >
              <ChevronRight className="w-5 h-5 text-ink" />
            </button>
          </div>

          {/* Quick day jumpers - Extended range for mobile */}
          <div className="flex gap-1 overflow-x-auto pb-2">
            {Array.from({ length: 15 }, (_, i) => i - 2).map(offset => {
              const date = new Date(currentMobileDay);
              date.setDate(date.getDate() + offset);
              const isToday = offset === 0;
              const isCurrentDay = date.toDateString() === currentMobileDay.toDateString();

              return (
                <button
                  key={offset}
                  onClick={() => {
                    const newDate = new Date(date);
                    setCurrentMobileDay(newDate);

                    // Update the weekly grid if we've navigated to a different week
                    const newWeekStart = getWeekStart(newDate);
                    if (newWeekStart.getTime() !== currentWeekStart.getTime()) {
                      setCurrentWeekStart(newWeekStart);
                    }
                  }}
                  className={`px-3 py-2 rounded-card text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    isCurrentDay
                      ? 'bg-ochre/15 text-ochre border border-ochre/40'
                      : 'bg-cream text-ink hover:bg-cream/70'
                  }`}
                >
                  {date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        /* Desktop Week Navigation */
        <div className="mb-6 flex items-center justify-between bg-paper border border-rule rounded-card p-4">
          <button
            onClick={() => navigateWeek('prev')}
            className="btn btn-secondary px-4 py-2"
          >
            ← Previous Week
          </button>

          <div className="text-center">
            <h2 className="text-lg font-semibold text-ink">
              Week of {formatDate(currentWeekStart.toISOString())}
            </h2>
          </div>

          <button
            onClick={() => navigateWeek('next')}
            className="btn btn-secondary px-4 py-2"
          >
            Next Week →
          </button>
        </div>
      )}

      {/* Loading State */}
      {gridLoading ? (
        <div className="bg-paper border border-rule rounded-card p-8">
          <div className="animate-pulse">
            {isMobile ? (
              /* Mobile Loading */
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-24 bg-cream rounded-card"></div>
                ))}
              </div>
            ) : (
              /* Desktop Loading */
              <div>
                <div className="grid grid-cols-7 gap-4 mb-4">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="h-6 bg-cream rounded"></div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-4">
                  {Array.from({ length: 35 }).map((_, i) => (
                    <div key={i} className="h-16 bg-cream rounded"></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : isMobile ? (
        /* Mobile Day View */
        <div
          className="bg-paper border border-rule rounded-card overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {(() => {
            const currentDayData = getCurrentDayData();

            if (!currentDayData) {
              return (
                <div className="p-8 text-center text-muted">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium text-ink mb-2">No availability data</h3>
                  <p className="text-sm">No scheduling information available for this day.</p>
                </div>
              );
            }

            if (!Array.isArray(currentDayData.slots) || currentDayData.slots.length === 0) {
              return (
                <div className="p-8 text-center text-muted">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium text-ink mb-2">No availability</h3>
                  <p className="text-sm">No time slots are available for this day.</p>
                </div>
              );
            }

            return (
              <div className="p-4 space-y-3">
                {currentDayData.slots.map((slot) => (
                  <button
                    key={slot.id}
                    onClick={() => handleSlotClick(slot)}
                    disabled={!slot.available}
                    className={`
                      w-full p-4 rounded-card text-left transition-all border
                      ${slot.available
                        ? 'bg-cream border-rule hover:bg-ochre/10 hover:border-ochre/40 cursor-pointer'
                        : 'bg-cream border-rule opacity-50 cursor-not-allowed'
                      }
                      ${selectedSlot?.id === slot.id ? 'ring-2 ring-ochre border-ochre/40' : ''}
                      min-h-[80px] touch-manipulation
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Clock className="w-5 h-5 mr-2 text-ink" />
                          <span className="font-semibold text-lg text-ink">
                            {formatTime(slot.startTime)}
                          </span>
                        </div>
                        <div className="flex items-center mb-2">
                          <User className="w-4 h-4 mr-2 text-muted" />
                          <span className="font-medium text-ink">{slot.tutor.name}</span>
                        </div>
                        <div className="flex items-center text-sm text-muted">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ochre/15 text-ochre">
                            {slot.canBook1Hour && slot.canBook2Hours ? '1-2 hours' :
                             slot.canBook1Hour ? '1 hour' :
                             slot.canBook2Hours ? '2 hours' : 'Limited'}
                          </span>
                        </div>
                      </div>
                      {slot.subject && (
                        <div className="ml-4 text-right">
                          <div className="flex items-center text-sm text-muted">
                            <BookOpen className="w-4 h-4 mr-1" />
                            <span className="font-medium">{slot.subject.name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      ) : (
        /* Desktop Week View */
        <div className="bg-paper border border-rule rounded-card overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-cream">
            {Array.isArray(weeklyGrid) ? weeklyGrid.map((day, index) => (
              <div key={day.date} className="p-4 text-center border-r border-rule last:border-r-0">
                <div className="font-semibold text-ink">
                  {dayNames[index]}
                </div>
                <div className="text-sm text-muted mt-1">
                  {formatDate(day.date)}
                </div>
              </div>
            )) : null}
          </div>

          {/* Time Slots Grid */}
          <div className="grid grid-cols-7">
            {Array.isArray(weeklyGrid) ? weeklyGrid.map((day) => (
              <div key={day.date} className="border-r border-rule last:border-r-0">
                {!Array.isArray(day.slots) || day.slots.length === 0 ? (
                  <div className="p-4 text-center text-muted min-h-[200px] flex items-center justify-center">
                    <div>
                      <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No availability</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 p-2">
                    {Array.isArray(day.slots) ? day.slots.map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => handleSlotClick(slot)}
                        disabled={!slot.available}
                        className={`
                          w-full p-3 rounded-card text-left transition-all text-sm
                          ${slot.available
                            ? 'bg-cream border border-rule hover:bg-ochre/10 hover:border-ochre/40 cursor-pointer'
                            : 'bg-cream border border-rule opacity-50 cursor-not-allowed'
                          }
                          ${selectedSlot?.id === slot.id ? 'ring-2 ring-ochre' : ''}
                        `}
                      >
                        <div className="flex items-center mb-1">
                          <Clock className="w-3 h-3 mr-1" />
                          <span className="font-medium">
                            {formatTime(slot.startTime)}
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-muted">
                          <User className="w-3 h-3 mr-1" />
                          <span>{slot.tutor.name}</span>
                        </div>
                        <div className="flex items-center text-xs text-muted mt-1">
                          <span>
                            {slot.canBook1Hour && slot.canBook2Hours ? '1-2hr available' :
                             slot.canBook1Hour ? '1hr available' :
                             slot.canBook2Hours ? '2hr available' : 'Limited availability'}
                          </span>
                        </div>
                        {slot.subject && (
                          <div className="flex items-center text-xs text-muted mt-1">
                            <BookOpen className="w-3 h-3 mr-1" />
                            <span>{slot.subject.name}</span>
                          </div>
                        )}
                      </button>
                    )) : null}
                  </div>
                )}
              </div>
            )) : null}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {selectedSlot && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-50">
          <div className={`bg-paper w-full ${
            isMobile
              ? 'fixed inset-0 overflow-y-auto'
              : 'rounded-card border border-rule max-w-lg relative p-6'
          }`}>
            {isMobile ? (
              /* Mobile Full-Screen Modal */
              <div className="min-h-full">
                {/* Mobile Header */}
                <div className="bg-paper border-b border-rule px-6 py-4 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-ink">Book Lesson</h3>
                    <button
                      onClick={() => {
                        setSelectedSlot(null);
                        setSelectedDuration(null);
                        setSelectedSubject1('');
                        setSelectedSubject2('');
                        setIsRepeatLesson(false);
                        setRepeatWeeks(4);
                        setIsOnline(false);
                      }}
                      className="p-2 rounded-card text-muted hover:text-ink hover:bg-cream"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Mobile Content */}
                <div className="px-6 py-6 pb-24">
                  <div className="space-y-6">
                    {/* Lesson Details - Mobile */}
                    <div className="bg-cream border border-rule rounded-card p-4">
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-ink">Date & Start Time</p>
                          <p className="text-lg font-semibold text-ink">
                            {formatDate(selectedSlot.date)} at {formatTime(selectedSlot.startTime)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-ink">Tutor</p>
                          <p className="text-lg font-semibold text-ink">{selectedSlot.tutor.name}</p>
                        </div>
                      </div>
                    </div>

                    {/* Duration Selection - Mobile */}
                    <div>
                      <label className="block text-lg font-semibold text-ink mb-4">
                        Lesson Duration *
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center p-4 border border-rule rounded-card cursor-pointer transition-colors hover:bg-cream">
                          <input
                            type="radio"
                            name="duration"
                            value="0.5"
                            checked={selectedDuration === 0.5}
                            onChange={() => setSelectedDuration(0.5)}
                            disabled={!selectedSlot.canBook30Min}
                            className="w-5 h-5 text-ochre focus:ring-ochre border-rule"
                          />
                          <div className="ml-4 flex-1">
                            <span className={`text-lg font-medium ${!selectedSlot.canBook30Min ? 'text-muted' : 'text-ink'}`}>
                              30 min
                            </span>
                            {!selectedSlot.canBook30Min && (
                              <p className="text-sm text-muted">Not available for this slot</p>
                            )}
                          </div>
                        </label>
                        <label className="flex items-center p-4 border border-rule rounded-card cursor-pointer transition-colors hover:bg-cream">
                          <input
                            type="radio"
                            name="duration"
                            value="1"
                            checked={selectedDuration === 1}
                            onChange={() => setSelectedDuration(1)}
                            disabled={!selectedSlot.canBook1Hour}
                            className="w-5 h-5 text-ochre focus:ring-ochre border-rule"
                          />
                          <div className="ml-4 flex-1">
                            <span className={`text-lg font-medium ${!selectedSlot.canBook1Hour ? 'text-muted' : 'text-ink'}`}>
                              1 Hour Lesson
                            </span>
                            {!selectedSlot.canBook1Hour && (
                              <p className="text-sm text-muted">Not available for this slot</p>
                            )}
                          </div>
                        </label>
                        <label className="flex items-center p-4 border border-rule rounded-card cursor-pointer transition-colors hover:bg-cream">
                          <input
                            type="radio"
                            name="duration"
                            value="1.5"
                            checked={selectedDuration === 1.5}
                            onChange={() => setSelectedDuration(1.5)}
                            disabled={!selectedSlot.canBook90Min}
                            className="w-5 h-5 text-ochre focus:ring-ochre border-rule"
                          />
                          <div className="ml-4 flex-1">
                            <span className={`text-lg font-medium ${!selectedSlot.canBook90Min ? 'text-muted' : 'text-ink'}`}>
                              1:30
                            </span>
                            {!selectedSlot.canBook90Min && (
                              <p className="text-sm text-muted">Not available for this slot</p>
                            )}
                          </div>
                        </label>
                        <label className="flex items-center p-4 border border-rule rounded-card cursor-pointer transition-colors hover:bg-cream">
                          <input
                            type="radio"
                            name="duration"
                            value="2"
                            checked={selectedDuration === 2}
                            onChange={() => setSelectedDuration(2)}
                            disabled={!selectedSlot.canBook2Hours}
                            className="w-5 h-5 text-ochre focus:ring-ochre border-rule"
                          />
                          <div className="ml-4 flex-1">
                            <span className={`text-lg font-medium ${!selectedSlot.canBook2Hours ? 'text-muted' : 'text-ink'}`}>
                              2 Hour Lesson
                            </span>
                            {!selectedSlot.canBook2Hours && (
                              <p className="text-sm text-muted">Not available for this slot</p>
                            )}
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Subject Selection - Mobile */}
                    {(selectedDuration === 0.5 || selectedDuration === 1 || selectedDuration === 1.5) && (
                      <div>
                        <label className="block text-lg font-semibold text-ink mb-4">
                          Subject *
                        </label>
                        <select
                          value={selectedSubject1}
                          onChange={(e) => setSelectedSubject1(e.target.value)}
                          className="w-full p-4 text-lg border border-rule rounded-card focus:ring-2 focus:ring-ochre focus:border-ochre"
                          required
                        >
                          <option value="">Select a subject</option>
                          {Array.isArray(subjects) ? subjects.map(subject => (
                            <option key={subject.id} value={subject.id}>
                              {formatSubjectWithLevel(subject)}
                            </option>
                          )) : null}
                        </select>
                      </div>
                    )}

                    {selectedDuration === 2 && (
                      <div className="space-y-6">
                        <div>
                          <label className="block text-lg font-semibold text-ink mb-4">
                            First Hour Subject *
                          </label>
                          <select
                            value={selectedSubject1}
                            onChange={(e) => setSelectedSubject1(e.target.value)}
                            className="w-full p-4 text-lg border border-rule rounded-card focus:ring-2 focus:ring-ochre focus:border-ochre"
                            required
                          >
                            <option value="">Select first subject</option>
                            {Array.isArray(subjects) ? subjects.map(subject => (
                              <option key={subject.id} value={subject.id}>
                                {formatSubjectWithLevel(subject)}
                              </option>
                            )) : null}
                          </select>
                        </div>
                        <div>
                          <label className="block text-lg font-semibold text-ink mb-4">
                            Second Hour Subject *
                          </label>
                          <select
                            value={selectedSubject2}
                            onChange={(e) => setSelectedSubject2(e.target.value)}
                            className="w-full p-4 text-lg border border-rule rounded-card focus:ring-2 focus:ring-ochre focus:border-ochre"
                            required
                          >
                            <option value="">Select second subject</option>
                            {Array.isArray(subjects) ? subjects.map(subject => (
                              <option key={subject.id} value={subject.id}>
                                {formatSubjectWithLevel(subject)}
                              </option>
                            )) : null}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Online/In-Person Selection - Mobile */}
                    <div>
                      <label className="block text-lg font-semibold text-ink mb-4">
                        Lesson Type *
                      </label>
                      <select
                        value={isOnline ? 'online' : 'in-person'}
                        onChange={(e) => setIsOnline(e.target.value === 'online')}
                        className="w-full p-4 text-lg border border-rule rounded-card focus:ring-2 focus:ring-ochre focus:border-ochre"
                      >
                        <option value="online">Online</option>
                        <option value="in-person">In-Person</option>
                      </select>
                    </div>

                    {/* Recurring Lesson Option - Mobile */}
                    <div>
                      <label className="flex items-start space-x-3 p-4 border border-rule rounded-card cursor-pointer transition-colors hover:bg-cream">
                        <input
                          type="checkbox"
                          checked={isRepeatLesson}
                          onChange={(e) => setIsRepeatLesson(e.target.checked)}
                          className="w-5 h-5 text-ochre focus:ring-ochre border-rule rounded mt-1"
                        />
                        <div className="flex-1">
                          <span className="text-lg font-semibold text-ink">
                            Book as recurring lesson
                          </span>
                          <p className="text-sm text-muted mt-1">
                            Same time every week for multiple weeks
                          </p>
                        </div>
                      </label>

                      {isRepeatLesson && (
                        <div className="mt-4">
                          <label className="block text-lg font-semibold text-ink mb-4">
                            Number of weeks
                          </label>
                          <select
                            value={repeatWeeks}
                            onChange={(e) => setRepeatWeeks(parseInt(e.target.value))}
                            className="w-full p-4 text-lg border border-rule rounded-card focus:ring-2 focus:ring-ochre focus:border-ochre"
                          >
                            {[4, 6, 8, 10, 12].map(weeks => (
                              <option key={weeks} value={weeks}>
                                {weeks} weeks
                              </option>
                            ))}
                          </select>
                          <p className="text-sm text-muted mt-2">
                            This will create {repeatWeeks} booking requests for consecutive weeks at the same time.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Notes - Mobile */}
                    <div>
                      <label className="block text-lg font-semibold text-ink mb-4">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={bookingNotes}
                        onChange={(e) => setBookingNotes(e.target.value)}
                        className="w-full p-4 text-lg border border-rule rounded-card focus:ring-2 focus:ring-ochre focus:border-ochre"
                        rows={4}
                        placeholder="Any specific topics or questions you'd like to focus on..."
                      />
                    </div>
                  </div>
                </div>

                {/* Mobile Footer */}
                <div className="fixed bottom-0 left-0 right-0 bg-paper border-t border-rule p-6">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setSelectedSlot(null);
                        setSelectedDuration(null);
                        setSelectedSubject1('');
                        setSelectedSubject2('');
                        setIsRepeatLesson(false);
                        setRepeatWeeks(4);
                        setIsOnline(false);
                      }}
                      className="flex-1 py-4 px-6 border border-rule rounded-card text-lg font-medium text-ink hover:bg-cream focus:outline-none focus:ring-2 focus:ring-rule"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBookingRequest}
                      disabled={loading || !selectedDuration ||
                        ((selectedDuration === 0.5 || selectedDuration === 1 || selectedDuration === 1.5) && !selectedSubject1) ||
                        (selectedDuration === 2 && (!selectedSubject1 || !selectedSubject2))}
                      className="flex-1 py-4 px-6 bg-ochre border border-ochre rounded-card text-lg font-medium text-white hover:bg-ochre/90 focus:outline-none focus:ring-2 focus:ring-ochre disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending...' : 'Request Booking'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Desktop Modal Content */
              <div>
                <h3 className="text-lg font-semibold text-ink mb-4">Book Lesson</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-sm text-muted">Date & Start Time</p>
                    <p className="font-medium text-ink">
                      {formatDate(selectedSlot.date)} at {formatTime(selectedSlot.startTime)}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-muted">Tutor</p>
                    <p className="font-medium text-ink">{selectedSlot.tutor.name}</p>
                  </div>

                  {/* Duration Selection */}
                  <div>
                    <label className="block text-sm font-medium text-ink mb-2">
                      Lesson Duration *
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="duration"
                          value="0.5"
                          checked={selectedDuration === 0.5}
                          onChange={() => setSelectedDuration(0.5)}
                          disabled={!selectedSlot.canBook30Min}
                          className="mr-2"
                        />
                        <span className={`${!selectedSlot.canBook30Min ? 'text-muted' : 'text-ink'}`}>
                          30 min {!selectedSlot.canBook30Min ? '(Not available)' : ''}
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="duration"
                          value="1"
                          checked={selectedDuration === 1}
                          onChange={() => setSelectedDuration(1)}
                          disabled={!selectedSlot.canBook1Hour}
                          className="mr-2"
                        />
                        <span className={`${!selectedSlot.canBook1Hour ? 'text-muted' : 'text-ink'}`}>
                          1 Hour Lesson {!selectedSlot.canBook1Hour ? '(Not available)' : ''}
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="duration"
                          value="1.5"
                          checked={selectedDuration === 1.5}
                          onChange={() => setSelectedDuration(1.5)}
                          disabled={!selectedSlot.canBook90Min}
                          className="mr-2"
                        />
                        <span className={`${!selectedSlot.canBook90Min ? 'text-muted' : 'text-ink'}`}>
                          1:30 {!selectedSlot.canBook90Min ? '(Not available)' : ''}
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="duration"
                          value="2"
                          checked={selectedDuration === 2}
                          onChange={() => setSelectedDuration(2)}
                          disabled={!selectedSlot.canBook2Hours}
                          className="mr-2"
                        />
                        <span className={`${!selectedSlot.canBook2Hours ? 'text-muted' : 'text-ink'}`}>
                          2 Hour Lesson {!selectedSlot.canBook2Hours ? '(Not available)' : ''}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Subject Selection for single-subject lessons */}
                  {(selectedDuration === 0.5 || selectedDuration === 1 || selectedDuration === 1.5) && (
                    <div>
                      <label className="block text-sm font-medium text-ink mb-2">
                        Subject *
                      </label>
                      <select
                        value={selectedSubject1}
                        onChange={(e) => setSelectedSubject1(e.target.value)}
                        className="input w-full"
                        required
                      >
                        <option value="">Select a subject</option>
                        {Array.isArray(subjects) ? subjects.map(subject => (
                          <option key={subject.id} value={subject.id}>
                            {formatSubjectWithLevel(subject)}
                          </option>
                        )) : null}
                      </select>
                    </div>
                  )}

                  {/* Subject Selection for 2-hour lesson */}
                  {selectedDuration === 2 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-ink mb-2">
                          First Hour Subject *
                        </label>
                        <select
                          value={selectedSubject1}
                          onChange={(e) => setSelectedSubject1(e.target.value)}
                          className="input w-full"
                          required
                        >
                          <option value="">Select first subject</option>
                          {Array.isArray(subjects) ? subjects.map(subject => (
                            <option key={subject.id} value={subject.id}>
                              {formatSubjectWithLevel(subject)}
                            </option>
                          )) : null}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-ink mb-2">
                          Second Hour Subject *
                        </label>
                        <select
                          value={selectedSubject2}
                          onChange={(e) => setSelectedSubject2(e.target.value)}
                          className="input w-full"
                          required
                        >
                          <option value="">Select second subject</option>
                          {Array.isArray(subjects) ? subjects.map(subject => (
                            <option key={subject.id} value={subject.id}>
                              {formatSubjectWithLevel(subject)}
                            </option>
                          )) : null}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Online/In-Person Selection */}
                  <div>
                    <label className="block text-sm font-medium text-ink mb-2">
                      Lesson Type *
                    </label>
                    <select
                      value={isOnline ? 'online' : 'in-person'}
                      onChange={(e) => setIsOnline(e.target.value === 'online')}
                      className="input w-full"
                    >
                      <option value="online">Online</option>
                      <option value="in-person">In-Person</option>
                    </select>
                  </div>

                  {/* Recurring Lesson Option */}
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isRepeatLesson}
                        onChange={(e) => setIsRepeatLesson(e.target.checked)}
                        className="rounded border-rule text-ochre focus:ring-ochre"
                      />
                      <span className="text-sm font-medium text-ink">
                        Book as recurring lesson (same time every week)
                      </span>
                    </label>

                    {isRepeatLesson && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-ink mb-2">
                          Number of weeks
                        </label>
                        <select
                          value={repeatWeeks}
                          onChange={(e) => setRepeatWeeks(parseInt(e.target.value))}
                          className="input w-full"
                        >
                          {[4, 6, 8, 10, 12].map(weeks => (
                            <option key={weeks} value={weeks}>
                              {weeks} weeks
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-muted mt-1">
                          This will create {repeatWeeks} booking requests for consecutive weeks at the same time.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-ink mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={bookingNotes}
                      onChange={(e) => setBookingNotes(e.target.value)}
                      className="input w-full"
                      rows={3}
                      placeholder="Any specific topics or questions you'd like to focus on..."
                    />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setSelectedSlot(null);
                      setSelectedDuration(null);
                      setSelectedSubject1('');
                      setSelectedSubject2('');
                      setIsOnline(false);
                    }}
                    className="btn btn-secondary flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBookingRequest}
                    disabled={loading || !selectedDuration || 
                      ((selectedDuration === 0.5 || selectedDuration === 1 || selectedDuration === 1.5) && !selectedSubject1) ||
                      (selectedDuration === 2 && (!selectedSubject1 || !selectedSubject2))}
                    className="btn btn-primary flex-1"
                  >
                    {loading ? 'Sending...' : 'Request Booking'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 bg-paper border border-rule rounded-card p-4">
        <h3 className="font-semibold text-ink mb-3">Legend</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-cream border border-rule rounded mr-2"></div>
            <span className="text-ink">Available</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-cream border border-rule opacity-50 rounded mr-2"></div>
            <span className="text-ink">Unavailable</span>
          </div>
        </div>
        <p className="text-xs text-muted mt-2">
          Click on an available time slot to request a booking. The tutor will review and respond to your request.
        </p>
      </div>
    </div>
  );
};

export default SchedulingGrid;